import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { FiPlus, FiTrash2, FiCheckSquare, FiArrowLeft, FiSave } from "react-icons/fi";
import AsyncSelect from "react-select/async";
import Swal from "sweetalert2";

const PurchasingOrderFA = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [items, setItems] = useState([
    { local_id: 1, id: null, name: "", price: 0, qty: 1, total: 0, drugs: [] },
  ]);
  const [distributors, setDistributors] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState("");
  const [selectedOutlet, setSelectedOutlet] = useState("");
  const [selectedMethod, setSelectedMethod] = useState("Cash");
  const [description, setDescription] = useState("");
  const [isAgreed, setIsAgreed] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    const storedIdOutlet = localStorage.getItem("id_outlet");
    const storedRole = (localStorage.getItem("role") || "").toLowerCase();

    if (storedRole === "manager" && storedIdOutlet) {
      setSelectedOutlet(storedIdOutlet);
    }
  }, []);

  const loadDistributors = async (inputValue) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/distributor`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          per_page: 30,
          page: 1,
          order_by: "name",
          search: inputValue || "",
        },
      });
      return (res.data?.data || []).map((d) => ({ value: d.id, label: `${d.name} (${d.kota || "Pusat"})` }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const loadOutlets = async (inputValue) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/outlet`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          per_page: 30,
          page: 1,
          order_by: "name",
          search: inputValue || "",
        },
      });
      return (res.data?.data || []).map((o) => ({ value: o.id, label: `${o.name} (${o.kota || "Pusat"})` }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const fetchDrugs = async (index, search = "") => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/v1/drug`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          per_page: 10,
          page: 1,
          order_by: "name",
          search,
        },
      });
      const newItems = [...items];
      newItems[index].drugs = res.data?.data || [];
      setItems(newItems);
    } catch (err) {
      console.error("Failed to fetch drugs:", err);
    }
  };

  const handleSearchChange = (index, value) => {
    const newItems = [...items];
    newItems[index].name = value;
    setItems(newItems);
    if (value.trim()) {
      fetchDrugs(index, value);
    }
  };

  const addItem = () => {
    setItems([
      ...items,
      { local_id: Date.now(), id: null, name: "", price: 0, qty: 1, total: 0, drugs: [] },
    ]);
  };

  const removeItem = (index) => {
    if (items.length <= 1) {
      Swal.fire({
        icon: "warning",
        title: "Peringatan",
        text: "Minimal harus ada 1 item obat dalam Surat Pesanan.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedDistributor || !selectedOutlet || items.some((i) => !i.id)) {
      Swal.fire({
        icon: "warning",
        title: "Form Belum Lengkap",
        text: "Pilih distributor, outlet penempatan, dan tentukan obat untuk setiap item!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    if (!isAgreed) {
      Swal.fire({
        icon: "warning",
        title: "Peringatan",
        text: "Anda harus mecentang persetujuan pembuatan Surat Pesanan ini.",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoadingSubmit(true);

    const payload = {
      distributor_id: parseInt(selectedDistributor, 10),
      outlet_id: parseInt(selectedOutlet, 10),
      method: selectedMethod.toLowerCase(),
      description,
      total_amount: items.reduce((sum, item) => sum + (item.total || 0), 0),
      items: items.map((item) => ({
        id: item.id,
        price: item.price,
        qty: item.qty,
        subtotal: item.total,
      })),
    };

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.status === 200 || res.status === 201) {
        await Swal.fire({
          title: "Berhasil Dibuat!",
          text: "Surat Pesanan (PO) berhasil dibuat.",
          icon: "success",
          timer: 1800,
          showConfirmButton: false,
          customClass: { popup: "rounded-3xl" },
        });

        navigate("/inventory/purchase-order");
      }
    } catch (err) {
      console.error("Failed to submit PO", err);
      Swal.fire({
        title: "Gagal Membuat PO",
        text: err.response?.data?.message || "Terjadi kesalahan server.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  const totalAmount = items.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/purchase-order")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Surat Pesanan (PO) Baru
            </h1>
            <p className="text-xs text-gray-500">
              Isi data distributor PBF, daftar obat, dan persetujuan Surat Pesanan
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Items Table */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
        >
          <div className="flex items-center justify-between pb-3 border-b border-gray-100">
            <h2 className="text-base font-extrabold text-gray-900 flex items-center gap-2">
              <FiCheckSquare className="text-emerald-600 w-5 h-5" />
              <span>Daftar Obat Yang Dipesan</span>
            </h2>
            <button
              onClick={addItem}
              className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-bold text-xs rounded-2xl transition-all"
            >
              <FiPlus className="w-4 h-4" />
              <span>Tambah Baris</span>
            </button>
          </div>

          <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
            <table className="w-full text-left text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50 text-gray-400 font-bold uppercase tracking-wider text-[10px]">
                  <th className="p-3.5 w-8">#</th>
                  <th className="p-3.5">Nama Obat & Cari</th>
                  <th className="p-3.5">Harga Beli</th>
                  <th className="p-3.5 w-24">Jumlah Qty</th>
                  <th className="p-3.5">Subtotal</th>
                  <th className="p-3.5 text-center w-12">Hapus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item, index) => (
                  <tr key={item.local_id || index} className="hover:bg-emerald-50/20 transition-colors">
                    <td className="p-3.5 text-center font-bold text-gray-400">{index + 1}</td>
                    <td className="p-3.5 relative">
                      <input
                        type="text"
                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-800 font-semibold focus:outline-none focus:border-emerald-500"
                        placeholder="Ketik & pilih nama obat..."
                        value={item.name}
                        onChange={(e) => handleSearchChange(index, e.target.value)}
                      />
                      {item.drugs && item.drugs.length > 0 && (
                        <ul className="absolute left-3.5 right-3.5 mt-1 bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-48 overflow-y-auto z-30 divide-y divide-gray-50">
                          {item.drugs.map((drug) => (
                            <li
                              key={drug.id}
                              className="p-3 hover:bg-emerald-50 cursor-pointer font-semibold text-xs text-gray-800 flex justify-between items-center"
                              onClick={() => {
                                const newItems = [...items];
                                newItems[index] = {
                                  local_id: item.local_id,
                                  id: drug.id,
                                  name: `${drug.name} ${drug.dosis || ""}`,
                                  price: drug.harga_beli || 0,
                                  qty: 1,
                                  total: drug.harga_beli || 0,
                                  drugs: [],
                                };
                                setItems(newItems);
                              }}
                            >
                              <span>{drug.name} {drug.dosis}</span>
                              <span className="font-mono text-emerald-600 font-bold">
                                Rp {(drug.harga_beli || 0).toLocaleString("id-ID")}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="p-3.5 font-mono font-semibold text-gray-700">
                      Rp {(item.price || 0).toLocaleString("id-ID")}
                    </td>
                    <td className="p-3.5">
                      <input
                        type="number"
                        min={1}
                        value={item.qty}
                        onChange={(e) => {
                          const newItems = [...items];
                          const val = Math.max(1, parseInt(e.target.value, 10) || 1);
                          newItems[index].qty = val;
                          newItems[index].total = (newItems[index].price || 0) * val;
                          setItems(newItems);
                        }}
                        className="w-full p-2 bg-gray-50 border border-gray-200 rounded-xl font-bold font-mono text-center text-xs focus:outline-none focus:border-emerald-500"
                      />
                    </td>
                    <td className="p-3.5 font-mono font-extrabold text-emerald-700">
                      Rp {(item.total || 0).toLocaleString("id-ID")}
                    </td>
                    <td className="p-3.5 text-center">
                      <button
                        onClick={() => removeItem(index)}
                        className="p-2 rounded-xl text-rose-500 hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {/* Right Column: PO Form Specs */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5 flex flex-col justify-between"
        >
          <div className="space-y-4">
            <h2 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3">
              Spesifikasi Surat Pesanan
            </h2>

            {/* Distributor Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Distributor (PBF)
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadDistributors}
                onChange={(opt) => setSelectedDistributor(opt?.value || "")}
                placeholder="Pilih Distributor PBF..."
                className="text-xs font-semibold"
              />
            </div>

            {/* Outlet Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Outlet Penempatan
              </label>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadOutlets}
                onChange={(opt) => setSelectedOutlet(opt?.value || "")}
                placeholder="Pilih Outlet..."
                className="text-xs font-semibold"
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Metode Pembayaran
              </label>
              <select
                value={selectedMethod}
                onChange={(e) => setSelectedMethod(e.target.value)}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              >
                <option value="Cash">Cash (Tunai)</option>
                <option value="Credit">Credit (Kredit/Tempo)</option>
              </select>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Catatan / Keterangan
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                placeholder="Isi catatan pengadaan obat..."
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all font-semibold"
              />
            </div>

            {/* Total Summary */}
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-600">Total Nominal SP</span>
              <span className="text-xl font-extrabold text-emerald-700">
                Rp {totalAmount.toLocaleString("id-ID")}
              </span>
            </div>

            {/* Agreement Checkbox */}
            <label className="flex items-center gap-2.5 text-xs text-gray-700 font-semibold cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={() => setIsAgreed(!isAgreed)}
                className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
              />
              <span>Saya menyetujui penerbitan Surat Pesanan ini</span>
            </label>
          </div>

          {/* Submit Button (GREEN BUTTON) */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSubmit}
            disabled={loadingSubmit}
            className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xs shadow-lg shadow-emerald-600/20 transition-all flex items-center justify-center gap-2"
          >
            <FiSave className="w-4 h-4" />
            <span>{loadingSubmit ? "Memproses PO..." : "Submit Purchasing Order"}</span>
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default PurchasingOrderFA;
